import type { Maybe } from '@/generated/graphql';
import { getGlobals, logger } from '@/lib';
import { APPLE_PAY_ELIGIBILITY_METRICS } from '@/lib/constants';
import { FEATURE_GUEST_APPLE_PAY_SHOWN } from '@/components/Landing/constants';
import { getApplePayIneligibilityReason } from '@/components/Landing/applepay/utils';
import type { ApplePayEligibilityResultLogging } from '@/components/Landing/types';

export const logServerExperimentationDetails = ({
  applePayButtonSeen,
  featureName,
  ineligibilityReason,
  isEligible,
  page,
}: {
  applePayButtonSeen?: Maybe<boolean>;
  featureName: string;
  ineligibilityReason: Array<string>;
  // The eligibility we send to FPTI for analytics is slightly different than
  // how we define eligibility client-side. For example, the user being either
  // in a treatment or control group doesn't affect FPTI eligibility.
  // The exception is when the user isn't in treatment and isn't in control.
  // i.e., the user doesn't fall under any of the experiment allocations.
  // In that case, the user will be FPTI ineligible.
  isEligible: boolean;
  page?: string;
}): void => {
  const experimentDetails = getGlobals().experiments[featureName];

  logger.fpti('checkout_server_experimentation_decision_occurred', {
    event_name: 'checkout_server_experimentation_decision_occurred',
    experimentation_experience: experimentDetails?.experimentId,
    experimentation_treatment: experimentDetails?.treatmentId,
    page,
    product: 'checkout',
    reason: JSON.stringify([
      {
        eligibility: isEligible ? 1 : 0,
        feature_name: featureName,
        reason: !isEligible ? ineligibilityReason : [],
        xe: experimentDetails?.experimentId,
        xt: experimentDetails?.treatmentId,
      },
    ]),
    state_name: 'ELMO_CHECK',
    transition_name: 'process_elmo_check',
    ...(applePayButtonSeen && { apple_pay: 'already_seen' }),
  });
};

export const logEligibilityDetails = ({
  ineligibilityReason,
  isEligible,
}: {
  ineligibilityReason: Array<string>;
  // The eligibility definition on our client is slightly different than
  // the eligibility that we send to FPTI for analytics.
  // Client eligibility signifies whether or not the user sees the experience.
  // e.g., whether or not we decide to render the Apple Pay button.
  isEligible: boolean;
}): void => {
  if (!isEligible) {
    const ineligibilityReasonStr = ineligibilityReason.toString();
    //  log individual ineligibility reasons
    ineligibilityReason.forEach(reason => {
      logger.metric(APPLE_PAY_ELIGIBILITY_METRICS.INELIGIBLE, {
        dimensions: {
          reason,
        },
      });
    });
    logger.warn(APPLE_PAY_ELIGIBILITY_METRICS.INELIGIBLE, {
      reason: ineligibilityReasonStr,
    });
  }
};

export const logEligibility = ({
  applePayButtonSeen,
  applePayButtonEligible,
  applePayButtonSeenOverride,
  applePayEligibilityResult,
  page,
}: ApplePayEligibilityResultLogging): void => {
  const { eligibility: isApplePayWithActiveCardEligible } =
    applePayEligibilityResult;

  const applePayButtonExperiment =
    getGlobals().experiments[FEATURE_GUEST_APPLE_PAY_SHOWN];
  // Treatment ID will only be populated
  // if user is in a treatment or control group.
  const hasTreatmentOrControl = Boolean(applePayButtonExperiment?.treatmentId);
  const isFptiEligible =
    isApplePayWithActiveCardEligible && hasTreatmentOrControl;

  logServerExperimentationDetails({
    applePayButtonSeen: applePayButtonSeen,
    featureName: FEATURE_GUEST_APPLE_PAY_SHOWN,
    ineligibilityReason: !isFptiEligible
      ? getApplePayIneligibilityReason({
          eligibilityResult: applePayEligibilityResult,
          isApplePayTreatment: applePayButtonEligible,
          isTreatmentOrControl: hasTreatmentOrControl,
        })
      : [],
    isEligible: isFptiEligible,
    page,
  });

  logEligibilityDetails({
    ineligibilityReason: !isApplePayWithActiveCardEligible
      ? getApplePayIneligibilityReason({
          applePayButtonSeen: applePayButtonSeen && !applePayButtonSeenOverride,
          eligibilityResult: applePayEligibilityResult,
          isApplePayTreatment: applePayButtonEligible,
        })
      : [],
    isEligible: isApplePayWithActiveCardEligible,
  });
};
